import { useCallback, useEffect, useState } from "react";
import Button from "../common/Button";
import { listApiKeys, createApiKey, revokeApiKey } from "../../api/apiKeys";
import { timeAgo } from "../../utils/contact";

// Keys for the quote API and MCP server. A key builds draft quotes and can ask
// for one to be sent; the send itself always waits for you on Follow-ups.

// Built from docs/quote-api/ by `npm run docs:pdf` and served from public/docs.
const GUIDES = [
  { file: "birdie-bus-mcp-server", title: "MCP server guide", blurb: "Connecting Claude and other AI assistants" },
  { file: "birdie-bus-quote-api", title: "REST API guide", blurb: "For developers connecting another app" },
];

const ACTIONS = {
  customer_created: "added a customer",
  quote_created: "built a draft",
  quote_updated: "changed a draft",
  quote_deleted: "deleted a draft",
  send_requested: "asked you to send",
  approved_and_sent: "you approved and sent",
  sent_back: "you sent it back",
};

export default function ApiAccessPanel({ Command }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null); // a key just made: shown once

  const load = useCallback(async () => {
    try {
      setData(await listApiKeys());
      setError("");
    } catch (e) {
      setError(e.message || "Couldn't load API keys.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      setFresh(await createApiKey(name.trim()));
      setName("");
      await load();
    } catch (err) {
      setError(err.message || "Couldn't make the key.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k) {
    if (!window.confirm(`Revoke “${k.name}”? Anything using it stops working straight away.`)) return;
    try {
      await revokeApiKey(k.id);
      if (fresh?.id === k.id) setFresh(null);
      await load();
    } catch (err) {
      setError(err.message || "Couldn't revoke it.");
    }
  }

  if (!data) return error ? <p className="text-sm text-red-700">{error}</p> : <p className="text-sm text-birdie-charcoal/50">Loading…</p>;

  const base = data.base_url;
  const key = fresh?.key || "bbk_YOUR_KEY";
  const live = data.keys.filter((k) => !k.revoked_at);
  const revoked = data.keys.filter((k) => k.revoked_at);

  return (
    <div className="space-y-4">
      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="rounded-xl bg-birdie-green/5 border border-birdie-green/15 px-3 py-2.5 text-xs text-birdie-charcoal/75">
        <strong className="text-birdie-charcoal">Nothing sends without you.</strong> A key can look up packages and dates, find or
        add customers, build and edit draft quotes, and ask you to send one. You approve on Follow-ups and get a phone alert. It
        can't send, take payments, see a draft's pay link, or touch quotes you built yourself.
      </div>

      <div>
        <div className="text-xs font-semibold text-birdie-charcoal/60 mb-1">Keys</div>
        {live.length === 0 && <p className="text-sm text-birdie-charcoal/55">No keys yet.</p>}
        <ul className="divide-y divide-birdie-green/10">
          {live.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-birdie-charcoal truncate">{k.name}</span>
                <span className="block text-[11px] text-birdie-charcoal/50">
                  {k.prefix}… · made {timeAgo(k.created_at)} · {k.last_used_at ? `last used ${timeAgo(k.last_used_at)}` : "never used"}
                  {k.actions_30d > 0 ? ` · ${k.actions_30d} action${k.actions_30d === 1 ? "" : "s"} this month` : ""}
                </span>
              </span>
              <button type="button" onClick={() => revoke(k)} className="shrink-0 text-xs font-semibold text-red-700">
                Revoke
              </button>
            </li>
          ))}
        </ul>
        {revoked.length > 0 && (
          <p className="text-[11px] text-birdie-charcoal/40 mt-1">Revoked: {revoked.map((k) => k.name).join(", ")}</p>
        )}
        <form onSubmit={create} className="flex gap-2 mt-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name it, e.g. Claude"
            maxLength={60}
            aria-label="New key name"
            className="min-w-0 flex-1 rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm"
          />
          <Button type="submit" size="sm" disabled={busy || !name.trim()}>
            {busy ? "Making…" : "Make a key"}
          </Button>
        </form>
      </div>

      {fresh && (
        <div className="rounded-xl border-2 border-birdie-mustard/60 bg-birdie-mustard/10 p-3">
          <div className="text-sm font-semibold text-birdie-charcoal">Your new key for “{fresh.name}”</div>
          <p className="text-xs text-birdie-charcoal/65 mb-2">Copy it now. It won't be shown again; if you lose it, revoke it and make another.</p>
          <Command text={fresh.key} />
        </div>
      )}

      <div className="space-y-3">
        <div className="text-xs font-semibold text-birdie-charcoal/60">Connect it</div>
        <div>
          <div className="text-sm text-birdie-charcoal">Claude Code</div>
          <Command text={`claude mcp add --transport http birdie-quotes ${base}/mcp --header "Authorization: Bearer ${key}"`} />
        </div>
        <div>
          <div className="text-sm text-birdie-charcoal">Claude Desktop</div>
          <p className="text-[11px] text-birdie-charcoal/50">Settings → Developer → Edit config, add this, then restart Claude.</p>
          <Command
            text={JSON.stringify(
              {
                mcpServers: {
                  "birdie-quotes": {
                    command: "npx",
                    // mcp-remote fills ${BIRDIE_KEY} in from env, which keeps the space out of the args.
                    args: ["-y", "mcp-remote", `${base}/mcp`, "--header", "Authorization:${BIRDIE_KEY}"], // eslint-disable-line no-template-curly-in-string
                    env: { BIRDIE_KEY: `Bearer ${key}` },
                  },
                },
              },
              null,
              2
            )}
          />
        </div>
        <div>
          <div className="text-sm text-birdie-charcoal">Any other app (REST)</div>
          <Command text={`curl ${base}/api/v1/packages -H "Authorization: Bearer ${key}"`} />
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-birdie-charcoal/60 mb-1">Guides</div>
        <ul className="grid gap-2 sm:grid-cols-2">
          {GUIDES.map((g) => (
            <li key={g.file} className="rounded-xl border border-birdie-green/15 bg-white px-3 py-2.5">
              <div className="text-sm font-semibold text-birdie-charcoal">{g.title}</div>
              <div className="text-[11px] text-birdie-charcoal/55">{g.blurb}</div>
              <div className="mt-2 flex items-center gap-3 text-xs font-semibold">
                <a href={`/docs/${g.file}.pdf`} download className="inline-flex items-center rounded-lg bg-birdie-green px-2.5 py-1 text-white">
                  Download PDF
                </a>
                <a href={`/docs/${g.file}.md`} download className="inline-flex items-center text-birdie-green underline">
                  Markdown
                </a>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-birdie-charcoal/55 mt-1.5">
          For code generators:{" "}
          <a href={`${base}/api/v1/openapi.json`} target="_blank" rel="noreferrer" className="underline">
            openapi.json
          </a>{" "}
          (OpenAPI 3.1)
        </p>
      </div>

      {data.events.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-birdie-charcoal/60 mb-1">Recent activity</div>
          <ul className="space-y-1">
            {data.events.map((e) => (
              <li key={e.id} className="flex justify-between gap-3 text-[12px]">
                <span className="min-w-0 truncate text-birdie-charcoal/75">
                  <strong className="text-birdie-charcoal">{e.key_name}</strong> {ACTIONS[e.action] || e.action}
                  {e.quote_number ? ` · ${e.quote_number}` : ""}
                  {e.detail ? ` · “${e.detail}”` : ""}
                </span>
                <span className="shrink-0 text-birdie-charcoal/45">{timeAgo(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
